import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from './ui/dialog';
import { projectsApi } from '../lib/api';
import { useOrg } from '../contexts/OrgContext';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Asked right after an inversion result is parsed (Insert > Inversion results > Import):
 * save it with a project, or only view it in this browser tab. The map's project is
 * preselected; without one the organisation's projects are offered.
 *
 *   open, name, fileName   - the parsed result
 *   projectId              - the map's project (preselected), or null
 *   onSave(projectId)      - store it with that project, then show it
 *   onSkip()               - show it without storing (closing the dialog does the same)
 */
export default function SaveModelDialog({ open, name, fileName, projectId = null, onSave, onSkip }) {
  const { C } = useTheme();
  const { selectedOrgId } = useOrg() || {};
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState(null);
  const [chosen, setChosen] = useState(projectId || '');

  useEffect(() => { if (open) { setChosen(projectId || ''); setError(null); } }, [open, projectId]);
  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    projectsApi.list(selectedOrgId)
      .then((r) => { if (live) setProjects(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (live) setError('Could not load the project list'); });
    return () => { live = false; };
  }, [open, selectedOrgId]);

  // The map's project is offered even when the list has not loaded (or does not contain it).
  const options = projectId && !projects.some((p) => p.id === projectId)
    ? [{ id: projectId, name: 'Current project' }, ...projects]
    : projects;
  const selStyle = { width: '100%', fontSize: 12, padding: '6px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onSkip?.(); }}>
      <DialogContent data-testid="save-model-dialog">
        <DialogHeader>
          <DialogTitle>Save inversion result?</DialogTitle>
          <DialogDescription>
            {name}{fileName && fileName !== name ? ` (${fileName})` : ''} was read. Saving stores it with a project so it
            survives a reload and appears under Stored models, on the dataset page and in the 3D viewer.
            View only keeps it in this browser tab.
          </DialogDescription>
        </DialogHeader>
        <div style={{ fontSize: 12, margin: '4px 0' }}>
          <label style={{ fontSize: 11, color: C.text2, display: 'block', marginBottom: 3 }}>Project</label>
          <select data-testid="save-model-project" value={chosen} onChange={(e) => setChosen(e.target.value)} style={selStyle}>
            {!chosen && <option value="">Choose a project…</option>}
            {options.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {error && <div style={{ fontSize: 11, color: C.red, marginTop: 4 }}>{error}</div>}
        </div>
        <DialogFooter>
          <button type="button" data-testid="save-model-skip" onClick={() => onSkip?.()} className="px-3 py-1 text-sm border rounded-md">View only</button>
          <button type="button" data-testid="save-model-save" disabled={!chosen} onClick={() => onSave?.(chosen)}
            className="px-3 py-1 text-sm rounded-md bg-primary text-primary-foreground disabled:opacity-50">Save to project</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
